"use client"

import React from "react"
import { rootPx } from "@/components/layout/RootPadding"
import { IconButton } from "../../button/IconButton"
import { useBottomSheetStore } from "../store/useBottomSheetStore"
import BottomSheetMenuItem, {
  BottomSheetMenuItemProps,
} from "./BottomSheetMenuItem"

type BottomSheetProps = {
  title?: React.ReactNode
  layout?: "fit" | "full"
  children: React.ReactNode
  onClose?: () => void
}

export const BottomSheet = ({
  title,
  children,
  layout: _layout,
  onClose,
}: BottomSheetProps) => {
  const { naviHeight, naviBackButton } = useBottomSheetStore()
  const layout = _layout ?? "fit"

  // 레이아웃 클래스 계산
  const layoutClasses = layout === "full" ? "h-full flex-1" : ""

  // 배경 및 스타일 클래스 계산
  const getBackgroundClasses = () => {
    if (naviHeight === 0) {
      // 전체 화면 모드
      if (naviBackButton) {
        return "bg-rl-gray-100" // 백 버튼이 있는 경우
      } else {
        return "bg-rl-common-white" // 백 버튼이 없는 경우
      }
    } else {
      // 일반 바텀시트 모드
      return "bg-rl-common-white rounded-t-xl pt-2.5"
    }
  }

  return (
    <>
      <div
        className={`mt-auto flex flex-col ${layoutClasses} ${getBackgroundClasses()}`}
      >
        {React.isValidElement(title)
          ? React.cloneElement(
              title as React.ReactElement<{ onClose?: () => void }>,
              { onClose },
            )
          : title}
        {children}
      </div>
    </>
  )
}

const { px } = rootPx
export const BottomSheetTitle = ({
  title,
  hasBottomLine,
  className,
  btnId,
  onClose,
}: {
  title: string
  hasBottomLine?: boolean
  className?: string
  btnId?: string
  onClose?: () => void
}) => {
  const { closeSheet, naviHeight, naviBackButton } = useBottomSheetStore()

  const handleClose = () => {
    if (onClose) {
      onClose()
    } else {
      closeSheet()
    }
  }

  // naviHeight가 0일 때는 백 버튼이 있는 네비게이션 스타일
  if (naviHeight === 0 && naviBackButton) {
    return (
      <div
        className={`bg-rl-common-white navibar-height relative flex w-full flex-shrink-0 items-center ${className && className}`}
      >
        <div className="absolute top-1/2 left-4 flex -translate-y-1/2 items-center gap-3">
          <IconButton
            id={btnId}
            src={"/images/icon/back_24.webp"}
            color={"bg-rl-gray-900"}
            ariaLabel={"뒤로가기"}
            iconSize={24}
            onClick={handleClose}
          />
          <div
            tabIndex={0}
            data-popup-focus="title"
            className="flex flex-1 items-center justify-center rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500"
          >
            <span className="text-rl-gray-900 typo-rl-body1 font-bold whitespace-pre-line">
              {title}
            </span>
          </div>
        </div>

        {hasBottomLine && (
          <div className="bg-rl-gray-200 absolute bottom-0 left-0 h-0.5 w-full" />
        )}
      </div>
    )
  } else if (naviHeight === 0 && !naviBackButton) {
    return (
      <div
        className={`${px} navibar-height mb-1 flex flex-shrink-0 flex-row items-center justify-between`}
      >
        <span
          tabIndex={0}
          data-popup-focus="title"
          className="text-rl-gray-900 typo-rl-headline1 font-bold whitespace-pre-line rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500"
        >
          {title}
        </span>
        <IconButton
          id={btnId}
          src={"/images/icon/close_24.webp"}
          color={"bg-rl-gray-900"}
          ariaLabel={"닫기"}
          iconSize={24}
          onClick={handleClose}
        />
      </div>
    )
  }

  // 기본 스타일 (naviHeight가 0이 아닐 때)
  return (
    <div
      className={`${px} ${hasBottomLine ? "border-rl-gray-200 border-b" : ""} mb-1 flex h-12 flex-shrink-0 flex-row items-center justify-between`}
    >
      <span
        tabIndex={0}
        data-popup-focus="title"
        className="text-rl-gray-900 typo-rl-headline1 font-bold whitespace-pre-line rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500"
      >
        {title}
      </span>
      <IconButton
        id={btnId}
        src={"/images/icon/close_24.webp"}
        color={"bg-rl-gray-900"}
        ariaLabel={"닫기"}
        iconSize={24}
        onClick={() => closeSheet()}
      />
    </div>
  )
}

type BottomSheetMenuProps = {
  title: string
  items: BottomSheetMenuItemProps[]
}

export const BottomSheetMenu = (props: BottomSheetMenuProps) => {
  const { items, title } = props
  return (
    <BottomSheet
      title={<BottomSheetTitle title={title} hasBottomLine={false} />}
    >
      <ol className="divide-rl-gray-200 divide-y overflow-y-scroll px-5 pb-6">
        {items.map((item, index) => (
          <BottomSheetMenuItem key={index} className="" {...item} />
        ))}
      </ol>
    </BottomSheet>
  )
}
